import { useState } from "react";

import { AlertTriangle } from "lucide-react";
import { Link } from "react-router-dom";
import classnames from "classnames";
import { ComponentSpinner } from "@components/spinner";
import StatusChip from "@components/ui-components/status-chip";
import PaginationBar from "@components/datatable/pagination-bar";
import useBrandRouteGuard from "../use-brand-route-guard";
import useBrandDashboard from "./use-brand-dashboard";
import PublisherDetailDrawer from "./publisher-detail-drawer";
import { formatCurrency } from "@src/utility/helpers/utils";
import { DEBIT_NOTE_STATUS_LABEL, DEBIT_NOTE_STATUS_TONE, KYC_LABEL, KYC_TONE, PAYOUT_STAGE_LABEL, PAYOUT_STAGE_TONE } from "@src/utility/constants/panel/common";
import { formatDate } from "@src/utility/helpers/date";

const ROWS_PER_PAGE_OPTIONS = [10, 25, 50];

const BrandDashboard = () => {
    const { brandId, brand } = useBrandRouteGuard();
    const {
        loading, currency, summary,
        publishers, publishersPagination, handlePublishersPageChange, handlePublishersRowsChange,
        paymentHistory, paymentHistoryPagination, handlePaymentHistoryPageChange, handlePaymentHistoryRowsChange
    } = useBrandDashboard(brandId);
    const [openPublisher, setOpenPublisher] = useState(null);

    if (loading) {
        return (
            <div className="flex justify-center py-24">
                <ComponentSpinner />
            </div>
        );
    }

    const owed = summary?.stats?.total_owed ?? 0;
    const pendingCount = summary?.stats?.publishers_pending_payout ?? 0;
    const currentDebitNote = summary?.current_debit_note ?? null;
    const kycIncompleteCount = summary?.kyc_alert?.not_completed ?? 0;
    const kycExpiringCount = summary?.kyc_alert?.expiring_soon ?? 0;
    // A self-payout brand has no debit notes, so neither the current-cycle tile
    // nor the history of past ones applies to it.
    const showDebitNotes = Boolean(brand?.hasDebitNotes);

    return (
        <div className="p-6">
            <h1 className="text-2xl font-semibold tracking-tight">Dashboard</h1>

            <div className={classnames("mt-6 grid gap-3", showDebitNotes ? "md:grid-cols-3" : "md:grid-cols-2")}>
                <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                    <p className="text-sm text-muted">Total owed to publishers</p>
                    <p className="mt-1 text-2xl font-semibold text-gray-900">{formatCurrency(owed, currency)}</p>
                    <p className="mt-1 text-xs text-muted">Sum of approved unpaid invoices</p>
                </div>

                <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                    <p className="text-sm text-muted">Publishers with pending payouts</p>
                    <p className="mt-1 text-2xl font-semibold text-gray-900">{pendingCount}</p>
                </div>

                {showDebitNotes && (
                    <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                        <p className="text-sm text-muted">Current debit note</p>
                        {currentDebitNote ? (
                            <>
                                <div className="mt-1 flex items-center justify-between gap-2">
                                    <span className="text-2xl font-semibold text-gray-900">{currentDebitNote.debit_note_number}</span>
                                    <StatusChip tone={DEBIT_NOTE_STATUS_TONE[currentDebitNote.status] || "neutral"} label={DEBIT_NOTE_STATUS_LABEL[currentDebitNote.status] || currentDebitNote.status} />
                                </div>
                                <Link to={`/brand/${brandId}/debit-note`} className="mt-2 inline-block text-xs font-medium text-accent hover:underline">
                                    View document →
                                </Link>
                            </>
                        ) : (
                            <p className="mt-1 text-2xl font-semibold text-muted">—</p>
                        )}
                    </div>
                )}
            </div>

            {kycIncompleteCount > 0 && (
                <div className="mt-4 flex items-start gap-2 rounded-lg border border-warning/40 bg-warning/10 p-3 text-sm">
                    <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-warning-foreground" />
                    <div>
                        {kycIncompleteCount} publisher{kycIncompleteCount > 1 ? "s have" : " has"} not completed KYC.
                        {kycExpiringCount > 0 && (
                            ` ${kycExpiringCount} publisher${kycExpiringCount > 1 ? "s'" : "'s"} KYC window expires soon.`
                        )}
                    </div>
                </div>
            )}

            <div className="mt-4 overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
                <div className="border-b border-gray-100 p-4 text-sm font-semibold text-gray-900">Publishers</div>
                <table className="w-full text-sm">
                    <thead className="bg-light text-left text-xs uppercase tracking-wide text-muted">
                        <tr>
                            <th className="p-3">Publisher</th>
                            <th className="p-3">Amount owed</th>
                            <th className="p-3">KYC</th>
                            <th className="p-3">Payout</th>
                            <th className="p-3">Confirmation ID</th>
                        </tr>
                    </thead>
                    <tbody>
                        {publishers.map((publisher) => (
                            <tr
                                key={publisher._id}
                                onClick={() => setOpenPublisher(publisher)}
                                className="cursor-pointer border-t border-gray-100 hover:bg-light">
                                <td className="p-3 font-medium text-gray-900">{publisher.holder_name}</td>
                                <td className="p-3 text-gray-700">{formatCurrency(publisher.pending_amount, currency)}</td>
                                <td className="p-3"><StatusChip tone={KYC_TONE[publisher.kyc_status] || "neutral"} label={KYC_LABEL[publisher.kyc_status] || publisher.kyc_status} /></td>
                                <td className="p-3"><StatusChip tone={PAYOUT_STAGE_TONE[publisher.payout_stage] || "neutral"} label={PAYOUT_STAGE_LABEL[publisher.payout_stage] || publisher.payout_stage} /></td>
                                <td className="p-3 text-xs text-muted">{publisher.confirmation_id || "—"}</td>
                            </tr>
                        ))}
                        {publishers.length === 0 && (
                            <tr><td colSpan={5} className="p-6 text-center text-sm text-muted">No publishers yet.</td></tr>
                        )}
                    </tbody>
                </table>
                {publishersPagination.total > publishersPagination.limit && (
                    <div className="border-t border-gray-100 p-2">
                        <PaginationBar
                            pageIndex={publishersPagination.page - 1}
                            pageSize={publishersPagination.limit}
                            total={publishersPagination.total}
                            rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
                            onPageChange={(pageIndex) => handlePublishersPageChange(publishersPagination.limit, pageIndex + 1)}
                            onRowsPerPageChange={handlePublishersRowsChange} />
                    </div>
                )}
            </div>

            {/* Every row here is a debit note, so the section goes with them. */}
            {showDebitNotes && (
                <div className="mt-4 overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
                    <div className="border-b border-gray-100 p-4 text-sm font-semibold text-gray-900">Payment history</div>
                    <table className="w-full text-sm">
                        <thead className="bg-light text-left text-xs uppercase tracking-wide text-muted">
                            <tr>
                                <th className="p-3">Period</th>
                                <th className="p-3">Debit note</th>
                                <th className="p-3">Total</th>
                                <th className="p-3">Status</th>
                            </tr>
                        </thead>
                        <tbody>
                            {paymentHistory.map((note) => (
                                <tr key={note._id} className="border-t border-gray-100">
                                    <td className="p-3 text-gray-700">{note.payout_period ? formatDate(note.payout_period, "MMMM YYYY") : "—"}</td>
                                    <td className="p-3 font-mono text-gray-900">{note.debit_note_number}</td>
                                    <td className="p-3 text-gray-900">{formatCurrency(note.amount, note.currency || currency)}</td>
                                    <td className="p-3">
                                        <StatusChip tone={DEBIT_NOTE_STATUS_TONE[note.status] || "neutral"} label={DEBIT_NOTE_STATUS_LABEL[note.status] || note.status} />
                                        {note.status === "reverted" && note.reason && (
                                            <p className="mt-1 text-xs text-muted">{note.reason}</p>
                                        )}
                                    </td>
                                </tr>
                            ))}
                            {paymentHistory.length === 0 && (
                                <tr><td colSpan={4} className="p-6 text-center text-sm text-muted">No past cycles.</td></tr>
                            )}
                        </tbody>
                    </table>
                    {paymentHistoryPagination.total > paymentHistoryPagination.limit && (
                        <div className="border-t border-gray-100 p-2">
                            <PaginationBar
                                pageIndex={paymentHistoryPagination.page - 1}
                                pageSize={paymentHistoryPagination.limit}
                                total={paymentHistoryPagination.total}
                                rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
                                onPageChange={(pageIndex) => handlePaymentHistoryPageChange(paymentHistoryPagination.limit, pageIndex + 1)}
                                onRowsPerPageChange={handlePaymentHistoryRowsChange} />
                        </div>
                    )}
                </div>
            )}

            <PublisherDetailDrawer
                open={Boolean(openPublisher)}
                onClose={() => setOpenPublisher(null)}
                publisher={openPublisher}
                currency={currency} />
        </div>
    );
};

export default BrandDashboard;
